import {isAxiosError} from 'axios';
import api from '@/api';
import {jsonApiRawMutate} from '@/utils/jsonapiClient';
import axios from '@/utils/axios';
import Sentry from '@/services/sentry/Sentry';
import mapKeysToCase from '@/utils/mapKeysToCase';
import FileUpload from '@/services/file-upload/FileUpload';
import {
  AssetMessageThread,
  Message,
  MessageAttachment,
  SendMessagePayload,
  SendMessageResult,
  UnifiedFeedItem,
  UnifiedFeedPage,
  UploadedAttachment,
} from './types';
import {MESSAGE_ATTACHMENTS_BUCKET} from './constants';

const MESSAGE_INCLUDES = [
  'sender',
  'message_attachments',
  'negotiation_snapshot',
  'negotiation_snapshot.gig_deliverable_changes',
  'negotiation_snapshot.brand_negotiation',
  'negotiation_snapshot.brand_negotiation.latest_snapshot',
  'negotiation_snapshot.brand_negotiation.latest_snapshot.gig_deliverable_changes',
];

const MESSAGE_TYPE = 'messages';
const MESSAGE_ATTACHMENT_TYPE = 'message_attachments';

function mapAttachment(raw: any): MessageAttachment {
  const mimeType: string = raw?.mimeType ?? '';
  return {
    id: String(raw?.id ?? ''),
    sourceId: raw?.sourceId ?? '',
    mimeType,
    fileName: raw?.fileName ?? '',
    viewableUrl: raw?.viewableUrl ?? '',
    downloadUrl: raw?.downloadUrl ?? '',
    isImage: /^image\//.test(mimeType),
  };
}

function deriveDisplayName(raw: any): string {
  const senderName: string = raw?.sender?.name ?? '';
  const signerName: string = raw?.signerDisplayName ?? '';
  if (raw?.sender?.kind === 'creator') {
    return signerName || senderName;
  }
  if (!signerName) return senderName;
  return `${signerName} from ${senderName}`;
}

function mapNegotiationSnapshot(raw: any) {
  if (!raw) return null;
  const {brandNegotiation} = raw;
  const latestSnapshotId = brandNegotiation?.latestSnapshot?.id;
  return {
    id: String(raw.id ?? ''),
    state: raw.state ?? 'pending',
    costInCents: raw.costInCents ?? null,
    previousCostInCents: raw.previousCostInCents ?? null,
    contentAgreementTimePeriodInMonths:
      raw.contentAgreementTimePeriodInMonths ?? null,
    previousContentAgreementTimePeriodInMonths:
      raw.previousContentAgreementTimePeriodInMonths ?? null,
    deliverableChanges: (raw.gigDeliverableChanges ?? []).map((c: any) => ({
      kind: c?.kind ?? '',
      oldRequiredCount: c?.oldRequiredCount ?? null,
      newRequiredCount: c?.newRequiredCount ?? null,
      campaignNetwork: c?.campaignNetwork ?? null,
    })),
    brandNegotiationId: brandNegotiation?.id
      ? String(brandNegotiation.id)
      : null,
    isLatestOffer:
      latestSnapshotId != null
        ? String(latestSnapshotId) === String(raw.id)
        : raw.state === 'pending' && brandNegotiation?.state === 'pending',
  };
}

export function mapMessage(raw: any): Message {
  const senderKind: string | null = raw?.sender?.kind ?? null;
  return {
    id: String(raw?.id ?? ''),
    body: raw?.body ?? '',
    bodyHtml: raw?.bodyHtml ?? null,
    createdAt: raw?.createdAt ?? '',
    signerDisplayName: raw?.signerDisplayName ?? null,
    contentSubmissionVersionId: raw?.contentSubmissionVersionId ?? null,
    sender: raw?.sender
      ? {
          id: String(raw.sender.id ?? ''),
          kind: senderKind,
          name: raw.sender.name ?? '',
          imageUrl: raw.sender.imageUrl ?? null,
        }
      : null,
    attachments: (raw?.messageAttachments ?? []).map(mapAttachment),
    negotiationSnapshot: mapNegotiationSnapshot(raw?.negotiationSnapshot),
    displayName: deriveDisplayName(raw),
    fromCurrentUser: senderKind === 'creator',
  };
}

export function mergePendingMessages(
  fetched: Message[],
  previous: Message[]
): Message[] {
  const fetchedIds = new Set(fetched.map((m) => m.id));
  const pending = previous.filter((m) => !fetchedIds.has(m.id));
  if (pending.length === 0) return fetched;
  return [...fetched, ...pending].sort((a, b) =>
    a.createdAt.localeCompare(b.createdAt)
  );
}

export function conversationMessagesQueryKey(conversationId: string) {
  return ['conversation-messages', conversationId];
}

export function versionMessagesQueryKey(versionId: string) {
  return ['version-messages', versionId];
}

export function unifiedMessagesQueryKey(
  conversationId: string,
  campaignId: string
) {
  return ['unified-messages', conversationId, campaignId];
}

export async function fetchConversationMessages(
  conversationId: string
): Promise<Message[]> {
  const response = await api.get(`/conversations/${conversationId}/messages`, {
    params: {include: MESSAGE_INCLUDES, order: 'asc'},
  });
  return ((response?.data as any[]) ?? []).map(mapMessage);
}

export async function fetchVersionMessages(
  versionId: string
): Promise<Message[]> {
  const response = await api.get(
    `/content_submission_versions/${versionId}/messages`,
    {params: {include: MESSAGE_INCLUDES, order: 'asc'}}
  );
  return ((response?.data as any[]) ?? []).map(mapMessage);
}

function mapUnifiedMessage(raw: any): Message {
  const senderType = raw?.sender?.type;
  return mapMessage({
    ...raw,
    sender: raw?.sender
      ? {
          ...raw.sender,
          kind: senderType === 'User' ? 'creator' : 'brand',
        }
      : null,
  });
}

function mapUnifiedFeedItem(raw: any): UnifiedFeedItem {
  if (raw?.type === 'version_thread') {
    const thread: AssetMessageThread = {
      versionId: String(raw.versionId),
      contentSubmissionId: String(raw.contentSubmissionId),
      parentContentSubmissionId: raw.parentContentSubmissionId
        ? String(raw.parentContentSubmissionId)
        : null,
      campaignName: raw.campaignName ?? undefined,
      thumbnailUrl: raw.thumbnailUrl ?? null,
      versionNumber: raw.versionNumber ?? 1,
      contentType: raw.contentType ?? '',
      status: raw.status ?? '',
      messageCount: raw.messageCount ?? 0,
      hasUnread: Boolean(raw.hasUnread),
      messages: (raw.messages ?? []).map(mapUnifiedMessage),
    };
    return {kind: 'versionThread', data: thread};
  }
  return {kind: 'message', data: mapUnifiedMessage(raw)};
}

export async function fetchUnifiedMessages(
  conversationId: string,
  campaignId: string,
  cursor?: string
): Promise<UnifiedFeedPage> {
  const response = await axios.get(
    `/conversations/${conversationId}/unified_feed`,
    {
      params: {
        campaign_id: campaignId,
        ...(cursor ? {cursor} : {}),
      },
    }
  );
  const data: any = mapKeysToCase(response.data);
  return {
    items: (data.data ?? []).map(mapUnifiedFeedItem),
    nextCursor: data.nextCursor ?? null,
    firstUnreadMessageId: data.firstUnreadMessageId ?? null,
  };
}

export async function uploadMessageAttachment(
  file: File,
  opts: {onProgress?: (pct: number) => void; signal?: AbortSignal} = {}
): Promise<UploadedAttachment> {
  try {
    const extension = file.name.includes('.')
      ? file.name.split('.').pop()!.toLowerCase()
      : file.type.split('/')[1];
    const {
      url,
      key,
      content_type: contentType,
    } = await FileUpload.getPresignedUploadUrl({
      bucket: MESSAGE_ATTACHMENTS_BUCKET,
      extension,
      file_name: file.name,
    });
    await FileUpload.upload({
      file,
      presignedUploadUrl: url,
      contentType: contentType || file.type,
      onProgress: opts.onProgress,
      signal: opts.signal,
    });
    return {
      sourceId: key.startsWith('/') ? key : `/${key}`,
      mimeType: file.type || contentType,
      fileName: file.name,
    };
  } catch (error) {
    if (!(isAxiosError(error) && error.response?.status === 422)) {
      const exception =
        error instanceof Error ? error : new Error(String(error));
      Sentry.captureException(exception, {
        message: 'Message attachment upload failed',
        fileName: file.name,
        mimeType: file.type,
      });
    }
    throw error;
  }
}

function mapCreatedMessage(doc: any): Message {
  const resource = doc?.data;
  const included: any[] = doc?.included ?? [];
  const findIncluded = (ref: any) =>
    ref
      ? included.find(
          (i) => i.type === ref.type && String(i.id) === String(ref.id)
        )
      : null;
  const senderRes = findIncluded(resource?.relationships?.sender?.data);
  const attachmentRes = (
    resource?.relationships?.messageAttachments?.data ?? []
  )
    .map(findIncluded)
    .filter(Boolean);
  return mapMessage({
    id: resource?.id,
    ...resource?.attributes,
    sender: senderRes ? {id: senderRes.id, ...senderRes.attributes} : null,
    messageAttachments: attachmentRes.map((a: any) => ({
      id: a.id,
      ...a.attributes,
    })),
  });
}

async function createMessage(path: string, body: string): Promise<Message> {
  const response = await jsonApiRawMutate(
    path,
    {data: {type: MESSAGE_TYPE, attributes: {body}}},
    'POST'
  );
  return mapCreatedMessage(response);
}

async function attachToMessage(
  messageId: string,
  attachment: UploadedAttachment
): Promise<void> {
  await jsonApiRawMutate(
    `/messages/${messageId}/attachments`,
    {
      data: {
        type: MESSAGE_ATTACHMENT_TYPE,
        attributes: {
          source_id: attachment.sourceId,
          mime_type: attachment.mimeType,
          file_name: attachment.fileName,
        },
      },
    },
    'POST'
  );
}

async function send(
  path: string,
  payload: SendMessagePayload
): Promise<SendMessageResult> {
  const message = await createMessage(path, payload.body);
  const results = await Promise.allSettled(
    (payload.attachments ?? []).map((attachment) =>
      attachToMessage(message.id, attachment)
    )
  );
  const failedAttachmentCount = results.filter(
    (r) => r.status === 'rejected'
  ).length;
  return {message, failedAttachmentCount};
}

export function sendConversationMessage(
  conversationId: string,
  payload: SendMessagePayload
): Promise<SendMessageResult> {
  return send(`/conversations/${conversationId}/messages`, payload);
}

export function sendVersionMessage(
  versionId: string,
  payload: SendMessagePayload
): Promise<SendMessageResult> {
  return send(`/content_submission_versions/${versionId}/messages`, payload);
}

export function markConversationRead(conversationId: string): Promise<unknown> {
  return api.put(`/conversations/${conversationId}/mark_read`, {});
}

export function markVersionMessagesRead(versionId: string): Promise<unknown> {
  return api.put(
    `/content_submission_versions/${versionId}/messages/mark_read`,
    {}
  );
}

export function markAllConversationMessagesRead(
  conversationId: string,
  campaignId: string
): Promise<unknown> {
  return axios.put(
    `/conversations/${conversationId}/mark_all_read`,
    undefined,
    {
      params: {campaign_id: campaignId},
    }
  );
}

export function assetThreadTarget(thread: AssetMessageThread): {
  submissionId: string;
  assetId: string;
  versionId: string;
} {
  return {
    submissionId:
      thread.parentContentSubmissionId ?? thread.contentSubmissionId,
    assetId: thread.contentSubmissionId,
    versionId: thread.versionId,
  };
}

export type NegotiationResponse = 'accept' | 'reject';

export function respondToNegotiation(
  action: NegotiationResponse,
  brandNegotiationId: string
): Promise<unknown> {
  return api.put(`/brand_negotiations/${brandNegotiationId}/${action}`, {});
}
